import {
  createWorkspaceInvitation,
  hasExistingInvitation,
} from "@/lib/utils/notificationUtils";
import useAuthStore from "@/store/authStore";
import {
  ChevronDown,
  Crown,
  Mail,
  MoreHorizontal,
  Search,
  Shield,
  UserMinus,
  UserPlus,
  Users2,
  X,
} from "lucide-react";
import PropTypes from "prop-types";
import { useEffect, useRef, useState } from "react";

import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import { Spinner } from "@/components/ui/spinner";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/hooks/useToast";
import { supabase } from "@/lib/supabase";
import { cn } from "@/lib/utils";
import { AnimatePresence, motion } from "framer-motion";

export default function ManageTeamDialog({
  isOpen,
  onOpenChange,
  workspace,
  initialTab = "invitations",
  onMembersChange,
}) {
  const { user } = useAuthStore();
  const { toast } = useToast();

  // Tab state
  const [activeTab, setActiveTab] = useState(initialTab);

  // Search state
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [inviteMessage, setInviteMessage] = useState("");
  const [isSending, setIsSending] = useState(false);

  // Members state
  const [members, setMembers] = useState([]);
  const [pendingInvites, setPendingInvites] = useState([]);
  const [isLoadingMembers, setIsLoadingMembers] = useState(false);
  const [updatingMemberId, setUpdatingMemberId] = useState(null);

  // Pagination
  const [hasMoreResults, setHasMoreResults] = useState(true);
  const [loadingMoreUsers, setLoadingMoreUsers] = useState(false);
  const resultsContainerRef = useRef(null);
  const PAGE_SIZE = 20;

  // Check if current user is owner
  const isOwner = workspace?.owner_id === user?.id;
  const currentUserMember = members.find((m) => m.id === user?.id);
  const isAdmin =
    isOwner || currentUserMember?.role === "admin" || currentUserMember?.role === "owner";

  // Initialize invite message
  useEffect(() => {
    if (workspace && user) {
      setInviteMessage(
        `Join me in collaborating on "${workspace.name}" - ${user.full_name || user.email}`
      );
    }
  }, [workspace, user]);

  // Reset on tab change or dialog open
  useEffect(() => {
    if (isOpen) {
      setActiveTab(initialTab);
      loadWorkspaceData();
    } else {
      setSearchQuery("");
      setSearchResults([]);
      setSelectedUsers([]);
    }
  }, [isOpen, initialTab]);

  // Load workspace members and pending invites
  const loadWorkspaceData = async () => {
    if (!workspace?.id) return;

    setIsLoadingMembers(true);
    try {
      // Load workspace members
      const workspaceMembers = await useAuthStore
        .getState()
        .getWorkspaceMembers(workspace.id);

      const membersWithProfiles = workspaceMembers.map((member) => ({
        ...member.profiles,
        role: member.role,
        permissions: member.permissions,
        workspace_member_id: member.id,
        user_id: member.user_id,
      }));

      setMembers(membersWithProfiles);

      // Load pending invites - split into two queries to avoid nested select issues
      const { data: pendingData, error: pendingError } = await supabase
        .from("notifications")
        .select("*")
        .eq("type", "workspace_invitation")
        .eq("workspace_id", workspace.id)
        .eq("status", "pending")
        .order("created_at", { ascending: false });

      if (!pendingError && pendingData && pendingData.length > 0) {
        // Get profiles for pending invite recipients
        const receiverIds = pendingData.map((p) => p.receiver_id).filter(Boolean);
        if (receiverIds.length > 0) {
          const { data: profiles } = await supabase
            .from("profiles")
            .select("id, full_name, email, avatar_url, settings")
            .in("id", receiverIds);

          const profileMap = new Map(profiles?.map((p) => [p.id, p]) || []);
          const pendingWithProfiles = pendingData.map((invite) => ({
            ...invite,
            profiles: profileMap.get(invite.receiver_id) || null,
          }));
          setPendingInvites(pendingWithProfiles);
        } else {
          setPendingInvites(pendingData);
        }
      } else {
        setPendingInvites([]);
      }
    } catch (error) {
      console.error("Error loading workspace data:", error);
    } finally {
      setIsLoadingMembers(false);
    }
  };

  // User search handler
  const handleUserSearch = async () => {
    if (!searchQuery.trim()) return;

    setIsSearching(true);
    setSearchResults([]);

    try {
      const { data: nameResults } = await supabase
        .from("profiles")
        .select("id, full_name, email, avatar_url, settings")
        .ilike("full_name", `%${searchQuery}%`)
        .neq("id", user.id)
        .range(0, PAGE_SIZE - 1)
        .order("full_name", { ascending: true });

      const { data: emailResults } = await supabase
        .from("profiles")
        .select("id, full_name, email, avatar_url, settings")
        .ilike("email", `%${searchQuery}%`)
        .neq("id", user.id)
        .range(0, PAGE_SIZE - 1)
        .order("full_name", { ascending: true });

      // Combine and dedupe
      const combined = [...(nameResults || [])];
      emailResults?.forEach((u) => {
        if (!combined.some((c) => c.id === u.id)) combined.push(u);
      });

      // Filter out existing members
      const memberIds = members.map((m) => m.id);
      const pendingIds = pendingInvites.map((p) => p.receiver_id);
      const filtered = combined.filter(
        (u) => !memberIds.includes(u.id) && !pendingIds.includes(u.id)
      );

      setSearchResults(filtered.slice(0, PAGE_SIZE));
      setHasMoreResults(filtered.length > PAGE_SIZE);
    } catch (error) {
      console.error("Search error:", error);
    } finally {
      setIsSearching(false);
    }
  };

  // Send invitations
  const handleSendInvitations = async () => {
    if (!workspace || !user || selectedUsers.length === 0) return;

    setIsSending(true);
    try {
      for (const recipient of selectedUsers) {
        const alreadyInvited = await hasExistingInvitation(
          recipient.id,
          workspace.id
        );
        if (alreadyInvited) continue;

        await createWorkspaceInvitation(
          String(recipient.id),
          String(user.id),
          {
            id: String(workspace.id),
            name: workspace.name || "",
            team_key: workspace.team_key || "",
            inviteMessage: inviteMessage || "",
          }
        );
      }

      toast({
        title: "Invitations sent",
        description: `Sent ${selectedUsers.length} invitation(s).`,
      });

      setSelectedUsers([]);
      setSearchResults([]);
      setSearchQuery("");
      loadWorkspaceData();
    } catch (error) {
      console.error("Error sending invitations:", error);
      toast({
        title: "Error sending invitations",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setIsSending(false);
    }
  };

  // Cancel invitation
  const handleCancelInvitation = async (inviteId) => {
    try {
      const { error } = await supabase
        .from("notifications")
        .update({ status: "cancelled" })
        .eq("id", inviteId);

      if (error) throw error;

      setPendingInvites((prev) => prev.filter((p) => p.id !== inviteId));
      toast({ title: "Invitation cancelled" });
    } catch (error) {
      console.error("Error cancelling invitation:", error);
      toast({
        title: "Error",
        description: "Failed to cancel invitation.",
        variant: "destructive",
      });
    }
  };

  // Change member role
  const handleChangeRole = async (memberId, userId, newRole) => {
    if (!isAdmin) return;

    setUpdatingMemberId(memberId);
    try {
      const { error } = await supabase
        .from("workspace_members")
        .update({ role: newRole, updated_at: new Date().toISOString() })
        .eq("id", memberId);

      if (error) throw error;

      setMembers((prev) =>
        prev.map((m) =>
          m.workspace_member_id === memberId ? { ...m, role: newRole } : m
        )
      );

      toast({
        title: "Role updated",
        description: `Member role changed to ${newRole}.`,
      });

      onMembersChange?.();
    } catch (error) {
      console.error("Error changing role:", error);
      toast({
        title: "Error",
        description: "Failed to update member role.",
        variant: "destructive",
      });
    } finally {
      setUpdatingMemberId(null);
    }
  };

  // Transfer ownership
  const handleTransferOwnership = async (memberId, userId) => {
    if (!isOwner) return;

    const confirmTransfer = window.confirm(
      "Are you sure you want to transfer ownership? This action cannot be undone."
    );
    if (!confirmTransfer) return;

    setUpdatingMemberId(memberId);
    try {
      // Update workspace owner
      const { error: wsError } = await supabase
        .from("workspaces")
        .update({ owner_id: userId, updated_at: new Date().toISOString() })
        .eq("id", workspace.id);

      if (wsError) throw wsError;

      // Update member roles
      await supabase
        .from("workspace_members")
        .update({ role: "owner" })
        .eq("workspace_id", workspace.id)
        .eq("user_id", userId);

      await supabase
        .from("workspace_members")
        .update({ role: "admin" })
        .eq("workspace_id", workspace.id)
        .eq("user_id", user.id);

      toast({
        title: "Ownership transferred",
        description: "You are now an admin of this project.",
      });

      loadWorkspaceData();
      onMembersChange?.();
    } catch (error) {
      console.error("Error transferring ownership:", error);
      toast({
        title: "Error",
        description: "Failed to transfer ownership.",
        variant: "destructive",
      });
    } finally {
      setUpdatingMemberId(null);
    }
  };

  // Remove member
  const handleRemoveMember = async (memberId, userId, memberName) => {
    if (!isAdmin || userId === user.id) return;

    const confirmRemove = window.confirm(
      `Remove ${memberName || "this member"} from the project?`
    );
    if (!confirmRemove) return;

    setUpdatingMemberId(memberId);
    try {
      const { error } = await supabase
        .from("workspace_members")
        .delete()
        .eq("id", memberId);

      if (error) throw error;

      // Also remove from workspace_members array
      const { data: ws } = await supabase
        .from("workspaces")
        .select("workspace_members")
        .eq("id", workspace.id)
        .single();

      if (ws?.workspace_members) {
        await supabase
          .from("workspaces")
          .update({
            workspace_members: ws.workspace_members.filter((id) => id !== userId),
          })
          .eq("id", workspace.id);
      }

      setMembers((prev) =>
        prev.filter((m) => m.workspace_member_id !== memberId)
      );

      toast({
        title: "Member removed",
        description: `${memberName || "Member"} has been removed from the project.`,
      });

      onMembersChange?.();
    } catch (error) {
      console.error("Error removing member:", error);
      toast({
        title: "Error",
        description: "Failed to remove member.",
        variant: "destructive",
      });
    } finally {
      setUpdatingMemberId(null);
    }
  };

  // Helper functions
  const getInitials = (name) => {
    if (!name) return "??";
    return name
      .split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase()
      .substring(0, 2);
  };

  const getAvatarUrl = (profile) => {
    return (
      profile?.avatar_url || profile?.settings?.profile?.avatar_url || null
    );
  };

  const getDisplayName = (profile) => {
    if (!profile) return "Unknown User";
    return (
      profile.settings?.profile?.display_name ||
      profile.full_name ||
      profile.email ||
      "Unknown User"
    );
  };

  const handleSelectUser = (selectedUser) => {
    setSelectedUsers((prev) => [...prev, selectedUser]);
    setSearchResults((prev) => prev.filter((r) => r.id !== selectedUser.id));
  };

  const handleRemoveSelectedUser = (userId) => {
    setSelectedUsers((prev) => prev.filter((u) => u.id !== userId));
  };

  const getRoleBadgeStyles = (role) => {
    switch (role) {
      case "owner":
        return "bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400 border-amber-200 dark:border-amber-800/50";
      case "admin":
        return "bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-400 border-purple-200 dark:border-purple-800/50";
      default:
        return "bg-gray-100 dark:bg-zinc-800 text-gray-700 dark:text-gray-400 border-gray-200 dark:border-zinc-700";
    }
  };

  // Handle dialog close properly
  const handleOpenChange = (open) => {
    onOpenChange(open);
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange} modal={true}>
      <DialogContent
        className="max-w-lg max-h-[85vh] bg-white dark:bg-zinc-900 border-gray-200 dark:border-zinc-800 rounded-2xl p-0 gap-0 flex flex-col overflow-hidden"
        hideCloseButton={true}
        onCloseAutoFocus={(e) => {
          // Prevent focus from going to a non-existent trigger element
          e.preventDefault();
          // Blur any focused element to release the focus trap
          setTimeout(() => {
            if (document.activeElement instanceof HTMLElement) {
              document.activeElement.blur();
            }
          }, 0);
        }}
      >
        <DialogTitle className="sr-only">Manage Team</DialogTitle>
        <DialogDescription className="sr-only">
          Invite members and manage your team
        </DialogDescription>

        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-zinc-800 flex-shrink-0">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
            Manage Team
          </h2>
          <Button
            variant="ghost"
            size="icon"
            onClick={() => handleOpenChange(false)}
            className="h-8 w-8 rounded-full"
          >
            <X className="h-4 w-4" />
          </Button>
        </div>

        {/* Tab Switcher */}
        <div className="p-4 pb-0">
          <div className="flex rounded-full bg-gray-100 dark:bg-zinc-800 p-1">
            <button
              onClick={() => setActiveTab("invitations")}
              className={cn(
                "flex-1 rounded-full py-2 px-4 text-sm font-medium transition-colors",
                activeTab === "invitations"
                  ? "bg-white dark:bg-zinc-700 text-gray-900 dark:text-white shadow-sm"
                  : "text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white"
              )}
            >
              Invitations
            </button>
            <button
              onClick={() => setActiveTab("members")}
              className={cn(
                "flex-1 rounded-full py-2 px-4 text-sm font-medium transition-colors flex items-center justify-center gap-2",
                activeTab === "members"
                  ? "bg-white dark:bg-zinc-700 text-gray-900 dark:text-white shadow-sm"
                  : "text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white"
              )}
            >
              Members
              {members.length > 0 && (
                <span className="bg-purple-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                  {members.length}
                </span>
              )}
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-auto p-4">
          {activeTab === "invitations" ? (
            <div className="space-y-4">
              {/* Search */}
              <div className="flex gap-2">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <Input
                    placeholder="Search users by name or email"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && handleUserSearch()}
                    className="pl-10"
                  />
                </div>
                <Button
                  onClick={handleUserSearch}
                  disabled={isSearching || !searchQuery.trim()}
                  variant="outline"
                >
                  {isSearching ? <Spinner className="h-4 w-4" /> : "Search"}
                </Button>
              </div>

              {/* Selected Users */}
              {selectedUsers.length > 0 && (
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                      Selected
                    </span>
                    <Badge variant="secondary">{selectedUsers.length}</Badge>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {selectedUsers.map((u) => (
                      <Badge
                        key={u.id}
                        variant="secondary"
                        className="py-1.5 pl-1.5 pr-2 flex items-center gap-1.5"
                      >
                        <Avatar className="h-5 w-5">
                          <AvatarImage src={getAvatarUrl(u)} />
                          <AvatarFallback className="text-[10px]">
                            {getInitials(u.full_name)}
                          </AvatarFallback>
                        </Avatar>
                        <span className="text-xs truncate max-w-[120px]">
                          {getDisplayName(u)}
                        </span>
                        <button
                          onClick={() => handleRemoveSelectedUser(u.id)}
                          className="ml-1 text-red-500 hover:text-red-700"
                        >
                          <X className="h-3 w-3" />
                        </button>
                      </Badge>
                    ))}
                  </div>
                </div>
              )}

              {/* Search Results */}
              <AnimatePresence>
                {(isSearching || searchResults.length > 0) && (
                  <motion.div
                    ref={resultsContainerRef}
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: "auto" }}
                    exit={{ opacity: 0, height: 0 }}
                    className="border border-gray-200 dark:border-zinc-700 rounded-lg max-h-[200px] overflow-y-auto"
                  >
                    {isSearching ? (
                      <div className="p-4 text-center">
                        <Spinner className="h-5 w-5 mx-auto text-purple-500" />
                      </div>
                    ) : searchResults.length > 0 ? (
                      <ul className="divide-y divide-gray-100 dark:divide-zinc-800">
                        {searchResults.map((u) => (
                          <li
                            key={u.id}
                            onClick={() => handleSelectUser(u)}
                            className="flex items-center justify-between p-3 hover:bg-gray-50 dark:hover:bg-zinc-800/50 cursor-pointer"
                          >
                            <div className="flex items-center gap-3">
                              <Avatar className="h-8 w-8">
                                <AvatarImage src={getAvatarUrl(u)} />
                                <AvatarFallback>
                                  {getInitials(u.full_name)}
                                </AvatarFallback>
                              </Avatar>
                              <div>
                                <p className="text-sm font-medium text-gray-800 dark:text-gray-200">
                                  {getDisplayName(u)}
                                </p>
                                <p className="text-xs text-gray-500">
                                  {u.email}
                                </p>
                              </div>
                            </div>
                            <UserPlus className="h-4 w-4 text-gray-400" />
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <div className="p-4 text-center text-gray-500">
                        No users found
                      </div>
                    )}
                  </motion.div>
                )}
              </AnimatePresence>

              {/* Invite Message */}
              {selectedUsers.length > 0 && (
                <div className="space-y-2">
                  <Textarea
                    value={inviteMessage}
                    onChange={(e) => setInviteMessage(e.target.value)}
                    placeholder="Add a personal message..."
                    className="min-h-[80px]"
                  />
                </div>
              )}

              {/* Pending Invitations */}
              {pendingInvites.length > 0 && (
                <div className="space-y-2 pt-4 border-t border-gray-200 dark:border-zinc-800">
                  <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 flex items-center gap-2">
                    <Mail className="h-4 w-4" />
                    Pending Invitations
                  </h3>
                  <ul className="space-y-2">
                    {pendingInvites.map((invite) => (
                      <li
                        key={invite.id}
                        className="flex items-center justify-between p-2 bg-gray-50 dark:bg-zinc-800/50 rounded-lg"
                      >
                        <div className="flex items-center gap-2">
                          <Avatar className="h-6 w-6">
                            <AvatarImage src={getAvatarUrl(invite.profiles)} />
                            <AvatarFallback className="text-[10px]">
                              {getInitials(invite.profiles?.full_name)}
                            </AvatarFallback>
                          </Avatar>
                          <span className="text-sm text-gray-700 dark:text-gray-300">
                            {invite.profiles?.email || "Unknown"}
                          </span>
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleCancelInvitation(invite.id)}
                          className="h-7 text-xs text-red-500 hover:text-red-700"
                        >
                          Cancel
                        </Button>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          ) : (
            // Members Tab
            <div className="space-y-4">
              {isLoadingMembers ? (
                <div className="flex items-center justify-center py-8">
                  <Spinner className="h-8 w-8 text-purple-500" />
                </div>
              ) : members.length === 0 ? (
                <div className="text-center py-8">
                  <Users2 className="h-12 w-12 mx-auto text-gray-400 mb-2" />
                  <p className="text-gray-500">No members yet</p>
                </div>
              ) : (
                <ul className="space-y-1 border border-gray-200 dark:border-zinc-700 rounded-lg overflow-hidden">
                  {members.map((member) => {
                    const isCurrentUser = member.id === user?.id;
                    const isMemberOwner = member.role === "owner";
                    const canManage = isAdmin && !isCurrentUser && !isMemberOwner;

                    return (
                      <li
                        key={member.id}
                        className="flex items-center justify-between p-3 hover:bg-gray-50 dark:hover:bg-zinc-800/50"
                      >
                        <div className="flex items-center gap-3">
                          <Avatar className="h-10 w-10">
                            <AvatarImage src={getAvatarUrl(member)} />
                            <AvatarFallback>
                              {getInitials(member.full_name)}
                            </AvatarFallback>
                          </Avatar>
                          <div>
                            <div className="flex items-center gap-2">
                              <p className="text-sm font-medium text-gray-800 dark:text-gray-200">
                                {getDisplayName(member)}
                              </p>
                              {isCurrentUser && (
                                <Badge
                                  variant="outline"
                                  className="text-[10px] px-1.5 py-0"
                                >
                                  You
                                </Badge>
                              )}
                            </div>
                            <p className="text-xs text-gray-500">
                              {member.email}
                            </p>
                          </div>
                        </div>

                        <div className="flex items-center gap-2">
                          <Badge
                            variant="outline"
                            className={cn(
                              "capitalize flex items-center gap-1",
                              getRoleBadgeStyles(member.role)
                            )}
                          >
                            {member.role === "owner" && (
                              <Crown className="h-3 w-3" />
                            )}
                            {member.role === "admin" && (
                              <Shield className="h-3 w-3" />
                            )}
                            {member.role}
                          </Badge>

                          {(canManage || (isOwner && !isCurrentUser)) && (
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="h-8 w-8"
                                  disabled={
                                    updatingMemberId ===
                                    member.workspace_member_id
                                  }
                                >
                                  {updatingMemberId ===
                                  member.workspace_member_id ? (
                                    <Spinner className="h-4 w-4" />
                                  ) : (
                                    <MoreHorizontal className="h-4 w-4" />
                                  )}
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                {/* Role change options */}
                                {member.role !== "admin" && (
                                  <DropdownMenuItem
                                    onClick={() =>
                                      handleChangeRole(
                                        member.workspace_member_id,
                                        member.user_id,
                                        "admin"
                                      )
                                    }
                                  >
                                    <Shield className="h-4 w-4 mr-2" />
                                    Promote to Admin
                                  </DropdownMenuItem>
                                )}
                                {member.role === "admin" && (
                                  <DropdownMenuItem
                                    onClick={() =>
                                      handleChangeRole(
                                        member.workspace_member_id,
                                        member.user_id,
                                        "member"
                                      )
                                    }
                                  >
                                    <ChevronDown className="h-4 w-4 mr-2" />
                                    Demote to Member
                                  </DropdownMenuItem>
                                )}

                                {/* Transfer ownership (owner only) */}
                                {isOwner && !isMemberOwner && (
                                  <>
                                    <DropdownMenuSeparator />
                                    <DropdownMenuItem
                                      onClick={() =>
                                        handleTransferOwnership(
                                          member.workspace_member_id,
                                          member.user_id
                                        )
                                      }
                                    >
                                      <Crown className="h-4 w-4 mr-2" />
                                      Transfer Ownership
                                    </DropdownMenuItem>
                                  </>
                                )}

                                {/* Remove member */}
                                <DropdownMenuSeparator />
                                <DropdownMenuItem
                                  onClick={() =>
                                    handleRemoveMember(
                                      member.workspace_member_id,
                                      member.user_id,
                                      getDisplayName(member)
                                    )
                                  }
                                  className="text-red-500 focus:text-red-500"
                                >
                                  <UserMinus className="h-4 w-4 mr-2" />
                                  Remove from Team
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          )}
                        </div>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <DialogFooter className="p-4 border-t border-gray-200 dark:border-zinc-800 flex-shrink-0">
          {activeTab === "invitations" && selectedUsers.length > 0 ? (
            <div className="flex items-center justify-between w-full">
              <span className="text-sm text-gray-600 dark:text-gray-400">
                {selectedUsers.length} user(s) ready to invite
              </span>
              <Button
                onClick={handleSendInvitations}
                disabled={isSending}
                className="bg-purple-600 hover:bg-purple-700 text-white"
              >
                {isSending ? (
                  <>
                    <Spinner className="h-4 w-4 mr-2" />
                    Sending...
                  </>
                ) : (
                  "Send Invitations"
                )}
              </Button>
            </div>
          ) : (
            <Button variant="outline" onClick={() => handleOpenChange(false)}>
              Close
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

ManageTeamDialog.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onOpenChange: PropTypes.func.isRequired,
  workspace: PropTypes.shape({
    id: PropTypes.string.isRequired,
    name: PropTypes.string,
    owner_id: PropTypes.string,
    team_key: PropTypes.string,
  }),
  initialTab: PropTypes.oneOf(["invitations", "members"]),
  onMembersChange: PropTypes.func,
};
